// TransactionManagement.tsx - Updated with minus sign for charges and proper modal
import React, { useState, useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAlert } from "../../contexts/AlertContext";
import { adminAPI } from "../../services/api";
import { Transaction } from "../../types";
import {
  MdPayments,
  MdSearch,
  MdCheckCircle,
  MdCancel,
  MdRefresh,
  MdPending,
  MdSwapHoriz,
  MdHistory,
  MdCardGiftcard,
  MdAccountBalance,
  MdTrendingUp,
  MdSavings,
  MdClose,
} from "react-icons/md";

const TransactionManagement = () => {
  const { addAlert } = useAlert();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<
    Transaction[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [balanceTypeFilter, setBalanceTypeFilter] = useState("all");
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);

  const PaymentsIcon = MdPayments as React.FC<React.SVGProps<SVGSVGElement>>;
  const SearchIcon = MdSearch as React.FC<React.SVGProps<SVGSVGElement>>;
  const CheckIcon = MdCheckCircle as React.FC<React.SVGProps<SVGSVGElement>>;
  const CancelIcon = MdCancel as React.FC<React.SVGProps<SVGSVGElement>>;
  const RefreshIcon = MdRefresh as React.FC<React.SVGProps<SVGSVGElement>>;
  const PendingIcon = MdPending as React.FC<React.SVGProps<SVGSVGElement>>;
  const TransferIcon = MdSwapHoriz as React.FC<React.SVGProps<SVGSVGElement>>;
  const HistoryIcon = MdHistory as React.FC<React.SVGProps<SVGSVGElement>>;
  const BonusIcon = MdCardGiftcard as React.FC<React.SVGProps<SVGSVGElement>>;
  const LedgerIcon = MdAccountBalance as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const StakeIcon = MdTrendingUp as React.FC<React.SVGProps<SVGSVGElement>>;
  const EarningsIcon = MdSavings as React.FC<React.SVGProps<SVGSVGElement>>;
  const CloseIcon = MdClose as React.FC<React.SVGProps<SVGSVGElement>>;

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getAllTransactions();
      setTransactions(response.data);
      setFilteredTransactions(response.data);
    } catch (error: any) {
      addAlert({ type: "error", message: "Failed to fetch transactions" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let filtered = transactions;

    if (searchTerm) {
      filtered = filtered.filter(
        (transaction) =>
          transaction.user?.first_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          transaction.user?.last_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          transaction.user?.email
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          transaction.txid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transaction.from_address
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          transaction.to_address
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          transaction.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (transaction) => transaction.status === statusFilter
      );
    }

    if (typeFilter !== "all") {
      filtered = filtered.filter(
        (transaction) => transaction.type === typeFilter
      );
    }

    if (balanceTypeFilter !== "all") {
      filtered = filtered.filter(
        (transaction) => transaction.balance_type === balanceTypeFilter
      );
    }

    setFilteredTransactions(filtered);
  }, [searchTerm, statusFilter, typeFilter, balanceTypeFilter, transactions]);

  const handleApproveTransaction = async (transactionId: number) => {
    try {
      await adminAPI.approveTransaction({ transaction_id: transactionId });
      addAlert({
        type: "success",
        message: "Transaction approved successfully",
      });
      fetchTransactions();
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to approve transaction",
      });
    }
  };

  const handleRejectTransaction = async (transactionId: number) => {
    const reason = prompt("Please enter the reason for rejection:");
    if (!reason?.trim()) {
      addAlert({ type: "error", message: "Rejection reason is required" });
      return;
    }

    try {
      await adminAPI.rejectTransaction({
        transaction_id: transactionId,
        reason,
      });
      addAlert({
        type: "success",
        message: "Transaction rejected successfully",
      });
      fetchTransactions();
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to reject transaction",
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "confirmed":
        return <CheckIcon className="w-4 h-4 text-green-500" />;
      case "pending":
        return <PendingIcon className="w-4 h-4 text-yellow-500" />;
      case "failed":
      case "rejected":
        return <CancelIcon className="w-4 h-4 text-red-500" />;
      default:
        return <PendingIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "failed":
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "deposit":
        return <CheckIcon className="w-4 h-4 text-green-500" />;
      case "withdrawal":
        return <CancelIcon className="w-4 h-4 text-red-500" />;
      case "charge":
        return <CancelIcon className="w-4 h-4 text-red-500" />;
      case "bonus":
        return <BonusIcon className="w-4 h-4 text-purple-500" />;
      case "transfer":
        return <TransferIcon className="w-4 h-4 text-blue-500" />;
      default:
        return <HistoryIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "deposit":
        return "text-green-600 dark:text-green-400";
      case "withdrawal":
      case "charge":
        return "text-red-600 dark:text-red-400";
      case "bonus":
        return "text-purple-600 dark:text-purple-400";
      case "transfer":
        return "text-blue-600 dark:text-blue-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  const getBalanceTypeIcon = (balanceType: string) => {
    switch (balanceType) {
      case "ledger":
        return <LedgerIcon className="w-4 h-4 text-blue-500" />;
      case "stake":
        return <StakeIcon className="w-4 h-4 text-green-500" />;
      case "bonus":
        return <BonusIcon className="w-4 h-4 text-purple-500" />;
      case "earnings":
        return <EarningsIcon className="w-4 h-4 text-orange-500" />;
      default:
        return <LedgerIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getBalanceTypeColor = (balanceType: string) => {
    switch (balanceType) {
      case "ledger":
        return "text-blue-600 dark:text-blue-400";
      case "stake":
        return "text-green-600 dark:text-green-400";
      case "bonus":
        return "text-purple-600 dark:text-purple-400";
      case "earnings":
        return "text-orange-600 dark:text-orange-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  // Format amount with minus sign for charges and withdrawals
  const formatAmount = (transaction: Transaction) => {
    const amount = Number(transaction.amount);
    const formattedAmount = amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    });

    // Add minus sign for charges and withdrawals
    if (transaction.type === "charge" || transaction.type === "withdrawal") {
      return `-${formattedAmount}`;
    }

    return formattedAmount;
  };

  const getAmountColor = (type: string) => {
    switch (type) {
      case "deposit":
      case "bonus":
        return "text-green-600 dark:text-green-400";
      case "withdrawal":
      case "charge":
        return "text-red-600 dark:text-red-400";
      case "transfer":
        return "text-blue-600 dark:text-blue-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  const pendingTransactions = transactions.filter(
    (t) => t.status === "pending"
  );

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div
        className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:items-start max-sm:gap-4`}
      >
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            Transaction Management
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            Review and manage all platform transactions.
          </p>
        </div>
        <button
          onClick={fetchTransactions}
          disabled={isLoading}
          className={`${GlobalStyles.buttonIconOutline} ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {/* Stats and Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 mb-6">
        <div className="lg:col-span-2">
          <div className={GlobalStyles.inputWrapper}>
            <SearchIcon className={GlobalStyles.inputIcon} />
            <input
              type="text"
              placeholder="Search transactions by user, TXID, address, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={GlobalStyles.input}
            />
          </div>
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={GlobalStyles.input}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="failed">Failed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={GlobalStyles.input}
          >
            <option value="all">All Types</option>
            <option value="deposit">Deposits</option>
            <option value="withdrawal">Withdrawals</option>
            <option value="charge">Charges</option>
            <option value="bonus">Bonuses</option>
            <option value="transfer">Transfers</option>
          </select>
        </div>
        <div>
          <select
            value={balanceTypeFilter}
            onChange={(e) => setBalanceTypeFilter(e.target.value)}
            className={GlobalStyles.input}
          >
            <option value="all">All Balances</option>
            <option value="ledger">Ledger</option>
            <option value="stake">Stake</option>
            <option value="bonus">Bonus</option>
            <option value="earnings">Earnings</option>
          </select>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-yellow-600`}>
            {pendingTransactions.length}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Pending
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className={GlobalStyles.tableContainer}>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className={`${CommonClasses.textGray} mt-4`}>
              Loading transactions...
            </p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-8">
            <PaymentsIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className={`${CommonClasses.textGray} mb-2`}>
              No transactions found
            </p>
            <p className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
              {searchTerm ||
              statusFilter !== "all" ||
              typeFilter !== "all" ||
              balanceTypeFilter !== "all"
                ? "Try adjusting your filters"
                : "No transactions recorded yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead className={GlobalStyles.tableHeader}>
                <tr>
                  <th className={GlobalStyles.tableHeaderCell}>User</th>
                  <th className={GlobalStyles.tableHeaderCell}>Type</th>
                  <th className={GlobalStyles.tableHeaderCell}>Amount</th>
                  <th className={GlobalStyles.tableHeaderCell}>Balance Type</th>
                  <th className={GlobalStyles.tableHeaderCell}>Coin/Network</th>
                  <th className={GlobalStyles.tableHeaderCell}>Description</th>
                  <th className={GlobalStyles.tableHeaderCell}>Status</th>
                  <th className={GlobalStyles.tableHeaderCell}>Date</th>
                  <th className={GlobalStyles.tableHeaderCell}>Actions</th>
                </tr>
              </thead>
              <tbody className={GlobalStyles.tableBody}>
                {filteredTransactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className={GlobalStyles.tableRow}
                    onClick={() => setSelectedTransaction(transaction)}
                  >
                    <td className={GlobalStyles.tableCell}>
                      <div>
                        <div
                          className={`font-medium ${CommonClasses.textGrayStrong}`}
                        >
                          {transaction.user?.first_name}{" "}
                          {transaction.user?.last_name}
                        </div>
                        <div
                          className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                        >
                          {transaction.user?.email}
                        </div>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-2`}>
                        {getTypeIcon(transaction.type)}
                        <span
                          className={`font-medium ${getTypeColor(
                            transaction.type
                          )}`}
                        >
                          {transaction.type.charAt(0).toUpperCase() +
                            transaction.type.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div
                        className={`font-semibold ${getAmountColor(
                          transaction.type
                        )}`}
                      >
                        {formatAmount(transaction)}{" "}
                        {transaction.coin
                          ? transaction.coin.toUpperCase()
                          : "USD"}
                      </div>
                      {transaction.fee > 0 && (
                        <div
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          Fee: {transaction.fee}
                        </div>
                      )}
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-2`}>
                        {getBalanceTypeIcon(transaction.balance_type)}
                        <span
                          className={`font-medium capitalize ${getBalanceTypeColor(
                            transaction.balance_type
                          )}`}
                        >
                          {transaction.balance_type}
                        </span>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      {transaction.coin ? (
                        <div>
                          <div
                            className={`font-medium ${CommonClasses.textGrayStrong}`}
                          >
                            {transaction.coin.toUpperCase()}
                          </div>
                          {transaction.network && (
                            <div
                              className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                            >
                              {transaction.network.toUpperCase()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className={`${CommonClasses.textGray}`}>-</span>
                      )}
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className="max-w-xs">
                        <p
                          className={`${CommonClasses.textSm} ${CommonClasses.textGrayStrong} line-clamp-2`}
                        >
                          {transaction.description || "No description"}
                        </p>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-2`}>
                        {getStatusIcon(transaction.status)}
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            transaction.status
                          )}`}
                        >
                          {transaction.status.charAt(0).toUpperCase() +
                            transaction.status.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div
                        className={`${CommonClasses.textSm} ${CommonClasses.textGrayStrong}`}
                      >
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </div>
                      <div
                        className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                      >
                        {new Date(transaction.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      {transaction.status === "pending" && (
                        <div className={`${CommonClasses.flexStart} gap-2`}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApproveTransaction(transaction.id);
                            }}
                            className={`${GlobalStyles.buttonIconPrimary}`}
                            title="Approve Transaction"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRejectTransaction(transaction.id);
                            }}
                            className={`${GlobalStyles.buttonIconDanger}`}
                            title="Reject Transaction"
                          >
                            <CancelIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      {transaction.status !== "pending" && (
                        <span
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          No actions
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transaction Stats */}
      {!isLoading && transactions.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className={`${GlobalStyles.sectionWrap} text-center`}>
            <div className={`${CommonClasses.textXl} font-bold text-primary`}>
              {transactions.length}
            </div>
            <div
              className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
            >
              Total
            </div>
          </div>
          <div className={`${GlobalStyles.sectionWrap} text-center`}>
            <div className={`${CommonClasses.textXl} font-bold text-green-600`}>
              {transactions.filter((t) => t.status === "confirmed").length}
            </div>
            <div
              className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
            >
              Confirmed
            </div>
          </div>
          <div className={`${GlobalStyles.sectionWrap} text-center`}>
            <div
              className={`${CommonClasses.textXl} font-bold text-yellow-600`}
            >
              {pendingTransactions.length}
            </div>
            <div
              className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
            >
              Pending
            </div>
          </div>
          <div className={`${GlobalStyles.sectionWrap} text-center`}>
            <div className={`${CommonClasses.textXl} font-bold text-red-600`}>
              {
                transactions.filter(
                  (t) => t.status === "failed" || t.status === "rejected"
                ).length
              }
            </div>
            <div
              className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
            >
              Failed
            </div>
          </div>
        </div>
      )}

      {/* Transaction Details Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-300 dark:border-gray-700">
              <div className={`${CommonClasses.flexBetween}`}>
                <h2
                  className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
                >
                  Transaction Details
                </h2>
                <button
                  onClick={() => setSelectedTransaction(null)}
                  className={`${GlobalStyles.buttonIconOutline}`}
                >
                  <CloseIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* User Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3
                    className={`${CommonClasses.textSm} font-medium ${CommonClasses.textGray} mb-3`}
                  >
                    User Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p
                        className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                      >
                        Name
                      </p>
                      <p className={CommonClasses.textGrayStrong}>
                        {selectedTransaction.user?.first_name}{" "}
                        {selectedTransaction.user?.last_name}
                      </p>
                    </div>
                    <div>
                      <p
                        className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                      >
                        Email
                      </p>
                      <p className={CommonClasses.textGrayStrong}>
                        {selectedTransaction.user?.email}
                      </p>
                    </div>
                    <div>
                      <p
                        className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                      >
                        User ID
                      </p>
                      <p className={CommonClasses.textGrayStrong}>
                        {selectedTransaction.user_id}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Transaction Information */}
                <div>
                  <h3
                    className={`${CommonClasses.textSm} font-medium ${CommonClasses.textGray} mb-3`}
                  >
                    Transaction Information
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          Type
                        </p>
                        <div className={`${CommonClasses.flexStart} gap-2`}>
                          {getTypeIcon(selectedTransaction.type)}
                          <p
                            className={`font-medium ${getTypeColor(
                              selectedTransaction.type
                            )}`}
                          >
                            {selectedTransaction.type.charAt(0).toUpperCase() +
                              selectedTransaction.type.slice(1)}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          Status
                        </p>
                        <div className={`${CommonClasses.flexStart} gap-2`}>
                          {getStatusIcon(selectedTransaction.status)}
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                              selectedTransaction.status
                            )}`}
                          >
                            {selectedTransaction.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          Balance Type
                        </p>
                        <div className={`${CommonClasses.flexStart} gap-2`}>
                          {getBalanceTypeIcon(selectedTransaction.balance_type)}
                          <p
                            className={`font-medium capitalize ${getBalanceTypeColor(
                              selectedTransaction.balance_type
                            )}`}
                          >
                            {selectedTransaction.balance_type}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          Amount
                        </p>
                        <p
                          className={`text-lg font-bold ${getAmountColor(
                            selectedTransaction.type
                          )}`}
                        >
                          {formatAmount(selectedTransaction)}{" "}
                          {selectedTransaction.coin
                            ? selectedTransaction.coin.toUpperCase()
                            : "USD"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedTransaction.description && (
                <div>
                  <h3
                    className={`${CommonClasses.textSm} font-medium ${CommonClasses.textGray} mb-2`}
                  >
                    Description
                  </h3>
                  <p
                    className={`${CommonClasses.textGrayStrong} p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border`}
                  >
                    {selectedTransaction.description}
                  </p>
                </div>
              )}

              {/* Transaction Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Coin & Network */}
                {(selectedTransaction.coin || selectedTransaction.network) && (
                  <div>
                    <h3
                      className={`${CommonClasses.textSm} font-medium ${CommonClasses.textGray} mb-3`}
                    >
                      Coin & Network
                    </h3>
                    <div className="space-y-2">
                      {selectedTransaction.coin && (
                        <div>
                          <p
                            className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                          >
                            Coin
                          </p>
                          <p className={CommonClasses.textGrayStrong}>
                            {selectedTransaction.coin.toUpperCase()}
                          </p>
                        </div>
                      )}
                      {selectedTransaction.network && (
                        <div>
                          <p
                            className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                          >
                            Network
                          </p>
                          <p className={CommonClasses.textGrayStrong}>
                            {selectedTransaction.network.toUpperCase()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Transaction ID & Fee */}
                <div>
                  <h3
                    className={`${CommonClasses.textSm} font-medium ${CommonClasses.textGray} mb-3`}
                  >
                    Transaction Details
                  </h3>
                  <div className="space-y-2">
                    {selectedTransaction.txid && (
                      <div>
                        <p
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          Transaction ID
                        </p>
                        <p
                          className={`${CommonClasses.textGrayStrong} font-mono text-sm break-all`}
                        >
                          {selectedTransaction.txid}
                        </p>
                      </div>
                    )}
                    {selectedTransaction.fee > 0 && (
                      <div>
                        <p
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          Fee
                        </p>
                        <p className={CommonClasses.textGrayStrong}>
                          {selectedTransaction.fee}{" "}
                          {selectedTransaction.coin
                            ? selectedTransaction.coin.toUpperCase()
                            : "USD"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Addresses */}
              {(selectedTransaction.from_address ||
                selectedTransaction.to_address) && (
                <div>
                  <h3
                    className={`${CommonClasses.textSm} font-medium ${CommonClasses.textGray} mb-3`}
                  >
                    Addresses
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedTransaction.from_address && (
                      <div>
                        <p
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          From Address
                        </p>
                        <p
                          className={`${CommonClasses.textGrayStrong} font-mono text-xs break-all bg-gray-50 dark:bg-gray-700 p-2 rounded`}
                        >
                          {selectedTransaction.from_address}
                        </p>
                      </div>
                    )}
                    {selectedTransaction.to_address && (
                      <div>
                        <p
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          To Address
                        </p>
                        <p
                          className={`${CommonClasses.textGrayStrong} font-mono text-xs break-all bg-gray-50 dark:bg-gray-700 p-2 rounded`}
                        >
                          {selectedTransaction.to_address}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3
                    className={`${CommonClasses.textSm} font-medium ${CommonClasses.textGray} mb-3`}
                  >
                    Dates
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <p
                        className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                      >
                        Created
                      </p>
                      <p className={CommonClasses.textGrayStrong}>
                        {new Date(
                          selectedTransaction.created_at
                        ).toLocaleString()}
                      </p>
                    </div>
                    {selectedTransaction.confirmed_at && (
                      <div>
                        <p
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          Confirmed
                        </p>
                        <p className={CommonClasses.textGrayStrong}>
                          {new Date(
                            selectedTransaction.confirmed_at
                          ).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Admin Actions for Pending Transactions */}
                {selectedTransaction.status === "pending" && (
                  <div>
                    <h3
                      className={`${CommonClasses.textSm} font-medium ${CommonClasses.textGray} mb-3`}
                    >
                      Admin Actions
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          handleApproveTransaction(selectedTransaction.id)
                        }
                        className={`${GlobalStyles.buttonPrimary} flex-1`}
                      >
                        <CheckIcon className="w-4 h-4 mr-2" />
                        Approve
                      </button>
                      <button
                        onClick={() =>
                          handleRejectTransaction(selectedTransaction.id)
                        }
                        className={`${GlobalStyles.buttonOutline} flex-1`}
                      >
                        <CancelIcon className="w-4 h-4 mr-2" />
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Admin Notes */}
              {selectedTransaction.admin_notes && (
                <div>
                  <h3
                    className={`${CommonClasses.textSm} font-medium ${CommonClasses.textGray} mb-2`}
                  >
                    Admin Notes
                  </h3>
                  <p
                    className={`${CommonClasses.textGrayStrong} p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg`}
                  >
                    {selectedTransaction.admin_notes}
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-300 dark:border-gray-700">
              <button
                onClick={() => setSelectedTransaction(null)}
                className={`${GlobalStyles.buttonPrimary} w-full`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionManagement;
